<template>
	<Tooltip :trigger="['hover']" :placement="placement" :title="tooltipTitle">
		<button
			type="button"
			:class="classes"
			:aria-label="menuItem.key"
			@click="emitClick"
		>
			<component :is="menuItemIcon" class="menu-item-icon"/>
		</button>
	</Tooltip>
</template>

<script>
import { computed, toRef } from 'vue';
import {
	HomeOutlined, ProfileOutlined,
	UserOutlined, ThunderboltOutlined, InfoCircleOutlined, SettingOutlined,
	QuestionCircleOutlined, LineChartOutlined, DashboardOutlined,
	BulbOutlined,
} from '@ant-design/icons-vue';
import { Tooltip } from 'ant-design-vue';

import Consts from '@/api/Consts';

import { useSmallScreen } from '@/utils/useSmallScreen';
import { useUserStore } from '@/stores/user';
import { useLicenseStore } from '@/stores/licenseStore';
import { useI18n } from 'vue-i18n';

import LoginIcon from '@/components/menu/LoginIcon.vue';
import LogoutIcon from '@/components/menu/LogoutIcon.vue';
import RoversIcon from '@/components/menu/RoversIcon.vue';
import CnetConstructorIcon from '@/components/menu/CnetConstructorIcon.vue';
import BaselinesIcon from '@/components/menu/BaselinesIcon.vue';


const HOME_MENU_ITEM_KEYS = ['home-map', 'home-public'];


const ICONS = {
	'home-map': HomeOutlined,
	'home-public': HomeOutlined,
	'bs-on-map': HomeOutlined,
	'account': UserOutlined,
	'base-stations': ProfileOutlined,
	'base-stations-edit': ProfileOutlined,
	'baselines': BaselinesIcon,
	'rovers': RoversIcon,
	'stats': LineChartOutlined,
	'stats-type': LineChartOutlined,
	'events': ThunderboltOutlined,
	'info': InfoCircleOutlined,
	'settings': SettingOutlined,
	'dashboard': DashboardOutlined,
	'insights': BulbOutlined,
	'cnet-constructor': CnetConstructorIcon,
	'login': LoginIcon,
	'logout': LogoutIcon,
};

export default {
	emits: ['click'],
	components: {
		Tooltip,
		HomeOutlined, ProfileOutlined,
		UserOutlined, ThunderboltOutlined, InfoCircleOutlined, SettingOutlined,
		QuestionCircleOutlined, LineChartOutlined, DashboardOutlined,
		RoversIcon, LoginIcon, LogoutIcon, CnetConstructorIcon, BaselinesIcon,
		BulbOutlined,
	},
	props: ['menuItem'],
	setup(props, { emit }) {
		const { t } = useI18n();
		const userStore = useUserStore();
		const licenseStore = useLicenseStore();
		const { smallScreen } = useSmallScreen();
		const placement = computed(() => smallScreen.value ? 'left' : 'bottom');
		const menuItemRef = toRef(props, 'menuItem');
		const isSettingsSpecialMode = computed(() => (
			userStore.settingsSpecialModeActive ||
			(licenseStore.qaOverlay != null && licenseStore.qaOverlay.active)
		));
		const menuItemIcon = computed(() =>
			(menuItemRef.value.key in ICONS) ? ICONS[menuItemRef.value.key] : QuestionCircleOutlined
		);
		const tooltipTitle = computed(() => {
			if (menuItemRef.value.key === 'settings' && isSettingsSpecialMode.value) {
				return t('menu.settingsSpecialMode');
			}
			return t('menu.' + menuItemRef.value.key);
		});
		const classes = computed(() => {
			const arr = ['menu-item-container'];
			const isSettingsSpecialMode = (
				menuItemRef.value.key === 'settings' &&
				userStore.settingsSpecialModeActive
			);
			if (menuItemRef.value.selected) {
				arr.push('menu-item-container-selected');
			}
			if (HOME_MENU_ITEM_KEYS.includes(menuItemRef.value.key)) {
				arr.push('menu-item-container-home');
			}
			if (isSettingsSpecialMode) {
				arr.push('menu-item-container-settings-special');
				if (menuItemRef.value.selected) {
					arr.push('menu-item-container-settings-special-selected');
				}
			}
			return arr;
		});
		const emitClick = () => {
			emit('click');
		}
		return {
			emitClick,
			placement,
			menuItemIcon,
			tooltipTitle,
			classes,
			bgDefault: Consts.COLORS.dark0,
			settingsSpecialModeGradientOrange: Consts.COLORS.settingsSpecialModeGradientOrange,
			settingsSpecialModeGradientPurple: Consts.COLORS.settingsSpecialModeGradientPurple,
			settingsSpecialModeGradientOrangeSelected: Consts.COLORS.settingsSpecialModeGradientOrangeSelected,
			settingsSpecialModeGradientPurpleSelected: Consts.COLORS.settingsSpecialModeGradientPurpleSelected,
		};
	},
};
</script>

<style scoped>
.menu-item-icon {
	font-size: 24px;
}

@media (max-width: 900px) {
	.menu-item-icon {
		font-size: 18px;
	}
}

.menu-item-container {
	appearance: none;
	display: block;
	aspect-ratio: 1 / 1;
	cursor: pointer;
	margin: 8px;
	padding: 8px 8px 5px 8px;
	border: none;
	border-radius: 6px;
	font: inherit;
	line-height: inherit;
	text-align: inherit;
	color: white;
	background-color: v-bind(bgDefault);
	box-shadow: rgba(0,0,0,0.5) 0px 1px 5px;
}

.menu-item-container:hover:not(.menu-item-container-settings-special),
.menu-item-container-selected:not(.menu-item-container-settings-special) {
	background-color: var(--ant-primary-color, #1890ff);
	box-shadow: rgba(14, 27, 133, 0.5) 0px 1px 5px;
}

@keyframes settings-special-gradient-move {
	0% {
		background-position: 0% 0%;
	}
	100% {
		background-position: 1000% 1000%;
	}
}

.menu-item-container-settings-special {
	background-image: linear-gradient(
		135deg,
		v-bind(settingsSpecialModeGradientOrange) 0%,
		v-bind(settingsSpecialModeGradientPurple) 25%,
		v-bind(settingsSpecialModeGradientOrange) 50%,
		v-bind(settingsSpecialModeGradientPurple) 75%,
		v-bind(settingsSpecialModeGradientOrange) 100%
	);
	background-size: 1000% 1000%;
	background-color: v-bind(settingsSpecialModeGradientPurple);
	animation: settings-special-gradient-move 200s linear infinite;
	box-shadow: rgba(60, 20, 100, 0.45) 0px 1px 5px;
}

.menu-item-container-settings-special:hover {
	box-shadow: rgba(80, 40, 120, 0.55) 0px 1px 5px;
}

.menu-item-container-settings-special-selected {
	background-image: linear-gradient(
		135deg,
		v-bind(settingsSpecialModeGradientOrangeSelected) 0%,
		v-bind(settingsSpecialModeGradientPurpleSelected) 25%,
		v-bind(settingsSpecialModeGradientOrangeSelected) 50%,
		v-bind(settingsSpecialModeGradientPurpleSelected) 75%,
		v-bind(settingsSpecialModeGradientOrangeSelected) 100%
	);
	background-color: v-bind(settingsSpecialModeGradientPurpleSelected);
	box-shadow: rgba(100, 50, 160, 0.55) 0px 1px 5px;
}

.menu-item-container-settings-special-selected:hover {
	box-shadow: rgba(120, 70, 180, 0.6) 0px 1px 5px;
}
</style>
